import { useMemo } from "react";
import type { CalEvent, WorkingHours } from "../types";
import { moveModeFor, proposeChange, rescheduleChange, type CalendarChange } from "../domain/changes";
import { peopleToCheck } from "../domain/resolve";
import { dayKeyOf, dayLabel, rangeLabel } from "../domain/time";
import type { LoadSchedules } from "../hooks/useAvailability";
import { TimeFinder } from "./TimeFinder";
import styles from "./CalendarModule.module.css";

interface RescheduleViewProps {
  readonly event: CalEvent;
  readonly now: Date;
  readonly workingHours: WorkingHours;
  readonly token: string;
  /** Every address of your mailbox (see Self). */
  readonly selfEmails: ReadonlySet<string>;
  readonly loadSchedules: LoadSchedules;
  /** A time was chosen: stage the change. */
  readonly onPick: (change: CalendarChange, start: Date) => void;
}

/**
 * Pick a new time for a meeting (the mockup's reschedule finder). Your own
 * meeting moves and attendees are updated; someone else's gets a proposal.
 */
export function RescheduleView({ event, now, workingHours, token, selfEmails, loadSchedules, onPick }: Readonly<RescheduleViewProps>) {
  const mode = moveModeFor(event);
  const people = useMemo(() => peopleToCheck(event, selfEmails), [event, selfEmails]);
  const names = useMemo(() => {
    const map = new Map(event.attendees.map((a) => [a.email, a.name]));
    if (event.organizerEmail) map.set(event.organizerEmail, event.organizerName);
    return map;
  }, [event]);

  if (mode === "none") {
    return <div className={styles.hint}>This meeting's time can't be changed from AiVA. Open it in Outlook.</div>;
  }

  const invited = event.attendees.some((a) => a.type !== "resource");
  let confirmLabel = invited ? "Move and Update Attendees" : "Move";
  if (mode === "propose") confirmLabel = "Send Proposal";
  return (
    <TimeFinder
      summary={
        <div className={`${styles.card} ${styles.cardNote}`}>
          Currently{" "}
          <b>
            {dayLabel(dayKeyOf(event.start))} · {rangeLabel(event.start, event.end)}
          </b>
          {mode === "propose" ? `. ${event.organizerName} will get your proposal` : ""}
        </div>
      }
      people={people}
      names={names}
      durationMin={Math.round((event.end.getTime() - event.start.getTime()) / 60000)}
      moving={event}
      now={now}
      workingHours={workingHours}
      token={token}
      loadSchedules={loadSchedules}
      selectedLine="Selected — confirm below"
      pickedTitle="New Time"
      confirmLabel={confirmLabel}
      onConfirm={(start, end) =>
        onPick(mode === "propose" ? proposeChange(event, start, end) : rescheduleChange(event, start, end, "move"), start)
      }
    />
  );
}
